// Global credits store — one source of truth for the balance pill,
// the top-up modal, and any 402 "insufficient_credits" handler.
//
// Shape:
//   balance           : number | null (null = not yet loaded)
//   costPerAction     : { ai_chat_message: 15, pitch_report: 300, memo_generation: 60 }
//   ledger            : most recent 30 rows from /api/billing/credits
//   packs             : [{ code, credits, price_inr }]
//   topupOpen         : boolean — controls the top-up modal visibility
//   topupReason       : null | { action, cost, balance } — prefilled from 402

import { create } from 'zustand';
import axios from 'axios';
import { getBackendBaseUrl } from '../lib/backendUrl';
import { authHeaders } from '../lib/apiAuth';

const BACKEND_URL = getBackendBaseUrl();
const API = `${BACKEND_URL}/api`;

async function api(method, path, body) {
  const headers = authHeaders();
  const resp = await axios({
    method,
    url: `${API}${path}`,
    data: body,
    headers,
  });
  return resp.data;
}

export const useCreditsStore = create((set, get) => ({
  balance: null,
  costPerAction: { ai_chat_message: 15, pitch_report: 300, memo_generation: 60 },
  ledger: [],
  packs: [],
  loading: false,
  topupOpen: false,
  topupReason: null,
  topupBusy: false,
  topupError: null,

  async refresh() {
    set({ loading: true });
    try {
      const data = await api('get', '/billing/credits?limit=30');
      set({
        balance: data.balance,
        costPerAction: data.cost_per_action || get().costPerAction,
        ledger: data.ledger || [],
        loading: false,
      });
    } catch (e) {
      set({ loading: false });
    }
  },

  async loadPacks() {
    try {
      const packs = await api('get', '/billing/credits/packs');
      set({ packs });
    } catch (e) {
      // Non-fatal — leave packs empty; the modal will show an error state.
    }
  },

  // Optimistically adjust the balance after a successful action so the
  // pill updates instantly instead of waiting for the next /credits poll.
  applyDelta(delta) {
    const current = get().balance;
    if (typeof current === 'number') {
      set({ balance: Math.max(0, current + delta) });
    }
  },

  setBalance(balance) {
    if (typeof balance === 'number') set({ balance });
  },

  openTopup(reason = null) {
    if (!get().packs?.length) get().loadPacks();
    set({ topupOpen: true, topupReason: reason, topupError: null });
  },

  closeTopup() {
    set({ topupOpen: false, topupReason: null, topupError: null, topupBusy: false });
  },

  async purchasePack(packCode) {
    set({ topupBusy: true, topupError: null });
    try {
      const resp = await api('post', '/billing/credits/topup', { pack_code: packCode });

      // Dev-mode path: the backend has no Razorpay creds; credits were
      // granted synchronously. Refresh + close.
      if (resp.dev_mode) {
        await get().refresh();
        set({ topupBusy: false, topupOpen: false });
        return { ok: true, dev_mode: true };
      }

      // Real Razorpay path: open Checkout with the returned order id.
      // After payment.captured the webhook credits the account; we poll
      // /credits once to pick up the new balance.
      await openRazorpayCheckout({
        keyId: resp.key_id,
        orderId: resp.order_id,
        amount: resp.amount,
        currency: resp.currency,
        packName: resp.pack?.name,
        credits: resp.pack?.credits,
        onSuccess: async () => {
          // Small delay to let the webhook fire before we re-poll.
          setTimeout(() => get().refresh(), 3000);
          set({ topupBusy: false, topupOpen: false });
        },
        onDismiss: () => set({ topupBusy: false }),
      });
      return { ok: true };
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || 'Top-up failed';
      set({ topupBusy: false, topupError: String(msg) });
      return { ok: false, error: msg };
    }
  },
}));


// ── Razorpay Checkout helper ──────────────────────────────────

let _rzpScriptPromise = null;
function loadRazorpay() {
  if (_rzpScriptPromise) return _rzpScriptPromise;
  _rzpScriptPromise = new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true);
    s.onerror = () => reject(new Error('Razorpay SDK failed to load'));
    document.body.appendChild(s);
  });
  return _rzpScriptPromise;
}

async function openRazorpayCheckout({
  keyId, orderId, amount, currency, packName, credits, onSuccess, onDismiss,
}) {
  await loadRazorpay();
  const rzp = new window.Razorpay({
    key: keyId,
    amount,
    currency,
    name: 'KubAIr',
    description: `${packName || 'Credit pack'} — ${credits || ''} credits`,
    order_id: orderId,
    handler: (resp) => onSuccess?.(resp),
    modal: { ondismiss: () => onDismiss?.() },
    theme: { color: '#10b981' },
  });
  rzp.open();
}
